/**
 * ProjectThemeService
 *
 * Manages V2 theme snapshots attached to projects.
 * Themes are stored as subcollection: projects/{projectId}/projectThemes/{themeId}
 *
 * Pattern: Same snapshot approach as OnboardingService.themeSettingsV2
 * but supports multiple themes per project with an `isActive` flag.
 *
 * When a theme is added from the global `themesV2/` collection,
 * it's deep-cloned into this subcollection (independent snapshot).
 *
 * @module firebase-core/services
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import { mergeWriteReplacingFields } from '@modules/firebase-core/utils/mergeWriteReplacingFields';
import { stripUndefined } from '@modules/firebase-core/utils/stripUndefined';
import type { OnboardingThemeSettingsV2 } from '../converters/onboardingConverter';

/** Project theme = OnboardingThemeSettingsV2 snapshot + metadata */
export interface ProjectThemeV2 extends OnboardingThemeSettingsV2 {
  /** Document ID in subcollection */
  id: string;
  /** Display name */
  name: string;
  /** Whether this is the active theme for the project */
  isActive: boolean;
  /** ISO date when added to project */
  createdAt: string;
  /** ISO date when last updated */
  updatedAt: string;
}

export class ProjectThemeService {
  private readonly collectionName = 'projectThemes';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  // ============================================================================
  // READ
  // ============================================================================

  /**
   * List all project themes.
   */
  async list(projectId: string): Promise<ProjectThemeV2[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const q = query(collectionRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ ...d.data(), id: d.id }) as ProjectThemeV2);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.list');
      throw error;
    }
  }

  /**
   * Get single project theme by ID.
   */
  async get(projectId: string, themeId: string): Promise<ProjectThemeV2 | null> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        themeId
      );
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;
      return { ...docSnap.data(), id: docSnap.id } as ProjectThemeV2;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.get');
      throw error;
    }
  }

  /**
   * Get the active project theme (isActive: true).
   * Returns null if no theme is active.
   */
  async getActive(projectId: string): Promise<ProjectThemeV2 | null> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const q = query(collectionRef, where('isActive', '==', true), limit(1));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const d = snapshot.docs[0];
      return { ...d.data(), id: d.id } as ProjectThemeV2;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.getActive');
      throw error;
    }
  }

  // ============================================================================
  // CREATE
  // ============================================================================

  /**
   * Add a global theme to the project (deep clone + set as active).
   *
   * Clones full ThemeV2 data into subcollection. Sets as active
   * and deactivates any previously active theme.
   *
   * @param projectId - Project to add theme to
   * @param theme - Source theme data (from global themesV2/ collection)
   * @returns Created project theme
   */
  async add(
    projectId: string,
    theme: { id: string; name: string } & OnboardingThemeSettingsV2
  ): Promise<ProjectThemeV2> {
    try {
      // Deactivate current active theme first
      const currentActive = await this.getActive(projectId);
      if (currentActive) {
        await this.update(projectId, currentActive.id, { isActive: false });
      }

      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = doc(collectionRef);
      const now = new Date().toISOString();

      // Deep clone theme data (strip source id/metadata)
      const { id: _sourceId, name, ...themeSettings } = theme;

      const projectTheme: ProjectThemeV2 = {
        ...themeSettings,
        id: docRef.id,
        name,
        sourceThemeId: _sourceId,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(docRef, stripUndefined(projectTheme));
      return projectTheme;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.add');
      throw error;
    }
  }

  /**
   * Create a non-active project theme candidate.
   *
   * Used for generated drafts that must not replace the active project theme
   * until the user explicitly applies them.
   */
  async createCandidate(
    projectId: string,
    theme: { id?: string; name: string } & OnboardingThemeSettingsV2
  ): Promise<ProjectThemeV2> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = doc(collectionRef);
      const now = new Date().toISOString();
      const { id: sourceId, name, ...themeSettings } = theme;

      const projectTheme: ProjectThemeV2 = {
        ...themeSettings,
        id: docRef.id,
        name,
        sourceThemeId: themeSettings.sourceThemeId ?? sourceId,
        isActive: false,
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(docRef, stripUndefined(projectTheme));
      return projectTheme;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.createCandidate');
      throw error;
    }
  }

  // ============================================================================
  // UPDATE
  // ============================================================================

  /**
   * Update project theme fields.
   *
   * Fields are deep-merged (`merge: true`), except `animationSettings` and `flowDocument`:
   * whole-value snapshots, replaced as a whole when present (`mergeFields`), in the same
   * atomic batch.
   */
  async update(
    projectId: string,
    themeId: string,
    data: Partial<ProjectThemeV2>
  ): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        themeId
      );
      const batch = CoreFirebaseService.getInstance().batch();

      // CRITICAL: Use set with merge, NEVER updateDoc — same rule as every other
      // service in this module. animationSettings and flowDocument are whole-value
      // snapshots: a deep merge would keep keys/screens the new value lacks (a scheme
      // switch's deleted per-screen screenAnimation inside the template flowDocument
      // after None, AMV-AC-004 S3b), so both are replaced wholesale.
      mergeWriteReplacingFields(
        batch,
        docRef,
        { ...data, updatedAt: new Date().toISOString() } as Partial<ProjectThemeV2>,
        ['animationSettings', 'flowDocument'],
        stripUndefined
      );
      await batch.commit();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.update');
      throw error;
    }
  }

  /**
   * Set a theme as active (deactivates all others).
   */
  async setActive(projectId: string, themeId: string): Promise<void> {
    try {
      // Deactivate current
      const currentActive = await this.getActive(projectId);
      if (currentActive && currentActive.id !== themeId) {
        await this.update(projectId, currentActive.id, { isActive: false });
      }
      // Activate new
      await this.update(projectId, themeId, { isActive: true });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.setActive');
      throw error;
    }
  }

  // ============================================================================
  // DELETE
  // ============================================================================

  /**
   * Remove a project theme.
   */
  async delete(projectId: string, themeId: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        themeId
      );
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ProjectThemeService.delete');
      throw error;
    }
  }
}
